/**
 * 2분 진단 온보딩 — 건너뛰기 불가 팝업.
 *
 * 가짜 데모 투어를 대체. 첫 세션에 사용자 **본인 사업장**으로:
 *  ① 업종(+세부 업종) + 상시 근로자 수 입력(프리미엄 폼)
 *  ② 우리 업종 안전 브리핑 — 겁주기(미이행 지적)가 아니라 **도움되는 정보** 중심:
 *     · 동종업계 실제 사고사례 여러 건(펼치면 재해개요·원인·재발방지대책)
 *     · 최근 바뀐 법령 + 우리 사업장에 미치는 영향(인원 기준 동적)
 *  ③ 첫 안전일지(TBM)를 **무료(쿼터 미차감)** 로 생성
 *  ④ 완료 → 홈으로
 *
 * 닫기/건너뛰기 불가: Modal onClose=no-op + hideCloseButton, 완료 버튼만 진행 경로.
 *
 * 설계 의도: 방금 가입한 사용자에게 "당신은 안전관리자 미선임입니다" 같은 지적을 들이밀지 않는다.
 * 대신 (a) 동종업계 사고사례 (b) 최근 법령 변경 처럼 사업주가 실제로 궁금해할 정보를 준다.
 * 과태료·처벌은 "당신 미이행" 이 아니라 "법이 이렇게 바뀝니다(정보)" 프레임으로만 노출.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  FiArrowRight, FiCheck, FiAlertTriangle, FiZap, FiFileText,
  FiChevronLeft, FiChevronDown, FiClock, FiShield, FiBookOpen, FiSearch, FiClipboard,
} from 'react-icons/fi'
import Modal from '../../components/ui/Modal'
import Button from '../../components/ui/Button'
import Spinner from '../../components/ui/Spinner'
import { toast } from '../../hooks/useToast'
import { sitesApi } from '../../lib/sitesApi'
import { penaltyApi, type PenaltyExposure } from '../../lib/penaltyApi'
import { acSearch, type AcCase } from '../../pages/ac-datacenter/acDataCenterApi'
import { onboardingApi, type SetupJobStatus, type SetupTaskIn } from './onboardingApi'
import { useAuthStore } from '../../store/authStore'
import { DoneStep, GeneratingStep, RaReviewModal, TasksStep } from './TaskSetupSteps'
import { KSIC_OPTIONS, KSIC_SUB_OPTIONS } from '../../data/ksic'
import { workTypeRiskRank } from '../../data/taskPresets'
import type { KsicCategory, SiteIn, WorkersInfo, SiteFlags } from '../../types/sites'

const ZERO_WORKERS: WorkersInfo = {
  regular: 0, contract: 0, daily: 0, office: 0, field: 0,
  foreign: 0, minor: 0, pregnant: 0, senior: 0,
}
const DEFAULT_FLAGS: SiteFlags = {
  contracting: false, chemical: false, dangerous_machine: false,
  construction: false, night_shift: false,
}

// 업종 아이콘(간단 이모지) — 값 매핑, 없으면 기본.
const KSIC_ICON: Record<string, string> = {
  manufacturing: '🏭', construction: '🏗️', chemical: '⚗️', mining: '⛏️',
  electricity_gas: '⚡', environment: '♻️', agriculture: '🌾', transport: '🚚',
  wholesale_retail: '🛒', food_lodging: '🍽️', healthcare: '🏥', it: '💻',
  education: '📚', finance: '🏦', realestate: '🏢', professional: '🔬',
  facility_support: '🧹', arts_sports: '🎭', service: '🛠️', other: '📋',
}

/** 원 → 사람이 읽는 금액(만원/억원). */
function won(krw?: number | null): string {
  const v = Math.max(0, Math.round(krw ?? 0))
  if (v >= 100_000_000) {
    const eok = v / 100_000_000
    return `${Number.isInteger(eok) ? eok : eok.toFixed(1)}억원`
  }
  if (v >= 10_000) return `${Math.round(v / 10_000).toLocaleString()}만원`
  return `${v.toLocaleString()}원`
}
const dday = (d?: number | null) => (typeof d === 'number' ? (d < 0 ? `D+${-d}` : `D-${d}`) : '')

// ── 위험성평가 매트릭스(5x5) — 실제 riskMatrix.ts 규칙과 동일 ─────────────────
// 사고사례를 우리 위험성평가 항목으로 "반영"하는 실제 제품 기능(linked_accident_case_id)의
// 온보딩용 예시. 위험성 = 가능성 × 중대성, 등급 임계값·색상은 RiskAssessmentTab 과 일치.
type RiskLevel = '매우높음' | '높음' | '보통' | '낮음'
function grade5x5(score: number): RiskLevel {
  if (score >= 12) return '매우높음'   // 상
  if (score >= 8) return '높음'        // 중상
  if (score >= 4) return '보통'        // 중
  return '낮음'                        // 하
}
const LEVEL_STYLE: Record<RiskLevel, string> = {
  '매우높음': 'bg-red-500/15 text-red-600 border-red-400/40',
  '높음': 'bg-orange-500/15 text-orange-600 border-orange-400/40',
  '보통': 'bg-amber-500/15 text-amber-600 border-amber-400/40',
  '낮음': 'bg-emerald-500/15 text-emerald-600 border-emerald-400/40',
}
// 감소대책 5단계 우선순위(법정) 라벨.
const TIER_LABEL: Record<string, string> = {
  elimination: '제거', substitution: '대체', engineering: '공학적', administrative: '관리적', ppe: '보호구',
}

/** 사고사례 → 위험성평가 1행(예시) 도출. 실제 반영 시엔 AI가 산정하지만, 여기선 규칙 기반 예시. */
function deriveRA(c: AcCase) {
  const tag = `${c.category ?? ''} ${c.summary ?? ''}`
  const sev =
    /사망|폭발|추락|감전|질식|붕괴|매몰/.test(tag) ? 5 :
    /끼임|화상|절단|협착|충돌|맞음|깔림|골절/.test(tag) ? 4 : 3
  const freq = 3
  const score = freq * sev
  const rSev = Math.max(2, sev - 1)
  const rFreq = 1
  const rScore = rFreq * rSev
  const prev = c.prevention ?? ''
  const tier =
    /보호구|착용|보호|마스크|장갑|안전대|안전모|귀마개/.test(prev) ? 'ppe' :
    /설치|방호|덮개|자동|차단|국소배기|인터록|펜스|가드/.test(prev) ? 'engineering' :
    /교육|절차|점검|관리|표지|작업계획|매뉴얼|자격|배치/.test(prev) ? 'administrative' : 'administrative'
  return {
    freq, sev, score, level: grade5x5(score),
    rFreq, rSev, rScore, rLevel: grade5x5(rScore), tier,
  }
}

// ── 최근 법령 변경 (큐레이션) ─────────────────────────────────────────────
// "예시로 생성" 요구에 맞춰, 실제 개정 사실을 정확한 조문·시행일·금액으로 큐레이션한다.
// '우리 사업장 영향' 만 근로자 수로 동적 생성 → 개인화.
type LawChange = {
  id: string
  badge: string
  badgeTone: 'amber' | 'red'
  title: string
  article: string
  effective: string       // 시행일 표기(정적 fallback)
  liveEffective?: string   // penaltyApi 로 덮어쓸 라이브 시행일/D-day(있으면)
  what: string
  impact: string           // 우리 사업장 영향(동적, 항상 노출)
  action: string
  applicableNow: boolean    // 이미 적용 대상 → 정렬 우선
}

function buildLawChanges(workers: number, penalty: PenaltyExposure | null): LawChange[] {
  const w = workers > 0 ? workers : 0
  const ge50 = w >= 50

  const ra: LawChange = {
    id: 'ra_penalty',
    badge: '과태료 신설',
    badgeTone: 'amber',
    title: '위험성평가, 안 하면 과태료가 생깁니다',
    article: '산업안전보건법 제36조·제175조 (법률 제21374호)',
    effective: ge50 ? '상시 50인↑ — 2027.1.1 시행' : '상시 50인↓ — 2028.1.1 시행',
    liveEffective: penalty
      ? `${penalty.effective_date} 시행 · ${dday(penalty.d_day)}`
      : undefined,
    what:
      '그동안 처벌 규정이 없던 위험성평가에 과태료가 처음 신설됩니다. ' +
      '① 미실시 최대 1,000만원 · ② 근로자 미참여·미주지 500만원 · ③ 결과 미기록·미보존 300만원 — 합산 최대 1,800만원.',
    impact: ge50
      ? `상시 ${w}인 사업장은 2027년 1월 1일부터 즉시 적용됩니다. 지금부터 준비해 두면 안전합니다.`
      : (w > 0
          ? `상시 ${w}인 사업장은 2028년 1월 1일부터 적용됩니다. 아직 여유가 있을 때 습관을 만들어 두는 게 유리해요.`
          : '시행일은 상시 근로자 수(50인 기준)에 따라 2027 또는 2028년으로 갈립니다.'),
    action:
      '연 1회 위험성평가를 실시하고, 근로자를 참여시키고, 결과를 3년간 보존하면 과태료 대상에서 벗어납니다. ' +
      'Heinrich가 이 세 가지를 자동으로 만들고 증빙까지 남겨 드립니다.',
    applicableNow: false,
  }

  const cma: LawChange = {
    id: 'cma_5',
    badge: '적용 확대',
    badgeTone: 'red',
    title: '중대재해처벌법, 5인 이상이면 이미 적용됩니다',
    article: '중대재해처벌법 제3조·제6조 (2024.1.27 시행)',
    effective: '2024.1.27부터 상시 5인 이상 전 사업장',
    what:
      '2024년 1월 27일부터 상시근로자 5인 이상 모든 사업장에 중대재해처벌법이 적용됩니다. ' +
      '사망사고가 나면 "안전보건 확보 의무"를 다했는지를 따져 사업주에게 1년 이상 징역 또는 10억원 이하 벌금이 부과될 수 있습니다.',
    impact: w >= 5
      ? `상시 ${w}인 사업장은 이미 적용 대상입니다. 위험성평가·교육·점검 기록이 곧 형사 방어의 핵심 증거가 됩니다.`
      : (w > 0
          ? `상시 ${w}인이라 지금은 대상이 아니지만, 인원이 5인 이상이 되는 순간 즉시 적용됩니다.`
          : '상시 5인 이상이 되면 즉시 적용 대상이 됩니다.'),
    action:
      '"우리는 안전보건 의무를 이행했다"를 증명할 기록 체계(위험성평가·안전교육·점검 이력)를 갖추는 것이 사실상 유일한 방어책입니다.',
    applicableNow: w >= 5,
  }

  // 이미 적용 중인 것(중처법)을 먼저 — 사업주에게 더 임박한 리스크.
  return [cma, ra].sort((a, b) => Number(b.applicableNow) - Number(a.applicableNow))
}

type Step = 'input' | 'result' | 'tasks' | 'generating' | 'done'

// 단계 계측용 인덱스 — 미완료 이탈자가 어디까지 봤는지 서버(users.onboarding_last_step)에
// 남긴다. 값 부재(None)는 "모달을 한 번도 렌더하지 못함"(대시보드 미도달)과 구분된다.
const STEP_IDX: Record<Step, number> = { input: 0, result: 1, tasks: 2, generating: 3, done: 4 }
const STEP_TOTAL = 5

export default function DiagnosisOnboardingModal({ open, onComplete }: {
  open: boolean
  onComplete: () => void
}) {
  const qc = useQueryClient()
  const [step, setStep] = useState<Step>('input')
  const [busy, setBusy] = useState(false)

  // 단계 진입 계측 — 열리자마자 0(input)이 기록되어 '모달까지는 봤다'가 남고,
  // 이후 단계 전환마다 갱신. fire-and-forget(실패해도 온보딩 진행 무영향).
  useEffect(() => {
    if (!open) return
    onboardingApi.progress(STEP_IDX[step], STEP_TOTAL).catch(() => {})
  }, [open, step])

  const [ksic, setKsic] = useState<KsicCategory | ''>('')
  const [subIndustry, setSubIndustry] = useState('')
  const [workerCount, setWorkerCount] = useState('')
  const [companyName, setCompanyName] = useState('')
  const authUser = useAuthStore(s => s.user)
  const setAuthUser = useAuthStore(s => s.setUser)

  const [siteId, setSiteId] = useState<string | null>(null)
  const [raReviewId, setRaReviewId] = useState<string | null>(null)
  const [raReviewOpen, setRaReviewOpen] = useState(false)
  const [penalty, setPenalty] = useState<PenaltyExposure | null>(null)

  // 동종업계 사고사례(ac-datacenter 공개 검색) — Step 2 진입 시 별도 로드.
  const [accCases, setAccCases] = useState<AcCase[]>([])
  const [accLoading, setAccLoading] = useState(false)
  const [accError, setAccError] = useState(false)
  const [openCase, setOpenCase] = useState<Record<string, boolean>>({})
  const [openLaw, setOpenLaw] = useState<Record<string, boolean>>({})

  // 작업 등록 + 기준 서류 4종 무료 생성(작업 1개) 위저드 상태
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<SetupTaskIn[]>([])
  const [genTaskName, setGenTaskName] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<SetupJobStatus | null>(null)
  const pollBusy = useRef(false)

  // 무료 생성 대상은 항상 선택 목록 안의 1개 — 목록이 바뀌어 무효가 되면 위험서열
  // 1위 작업으로(백엔드 일일 TBM 우선 생성 순서와 동일 서열 → 기준 서류와 TBM 이 같은
  // 작업에 먼저 깔린다).
  useEffect(() => {
    if (tasks.length === 0) { setGenTaskName(null); return }
    if (!genTaskName || !tasks.some(t => t.name === genTaskName)) {
      const top = [...tasks].sort((a, b) => workTypeRiskRank(a.work_type) - workTypeRiskRank(b.work_type))[0]
      setGenTaskName(top.name)
    }
  }, [tasks, genTaskName])

  // ── 재방문 복원 (2026-08-19 실측 수정) ────────────────────────────────────
  // 이전엔 새로고침·페이지 이동마다 1단계부터 다시 시작했다(입력·브리핑 증발 +
  // 건너뛰기 불가 모달이라 사용자가 갇혔다). 복원 순서:
  // ① 생성 잡이 있으면 진행/완료 화면으로 ② 진단을 마친 사업장이 있으면 입력
  // 프리필 + 결과 화면으로 ③ 아무것도 없으면 가입 시 회사명만 프리필.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (!open || restoredRef.current) return
    restoredRef.current = true
    ;(async () => {
      try {
        const cur = await onboardingApi.setupCurrent().catch(() => null)
        if (cur?.job_id) {
          if (cur.site_id) setSiteId(cur.site_id)
          setJobId(cur.job_id)
          if (cur.status === 'done') {
            const st = await onboardingApi.setupStatus(cur.job_id).catch(() => null)
            if (st) setJobStatus(st)
            setStep('done')
          } else {
            setStep('generating')
          }
          return
        }
        const base = await sitesApi.ensureDefault().catch(() => null)
        if (base?.ksic_category) {
          setSiteId(base.id)
          setKsic(base.ksic_category as KsicCategory)
          const catLabel = KSIC_OPTIONS.find(o => o.value === base.ksic_category)?.label ?? ''
          if (base.ksic_name && base.ksic_name !== catLabel) setSubIndustry(base.ksic_name)
          if (base.workers?.regular) setWorkerCount(String(base.workers.regular))
          if (base.name && base.name !== '내 사업장') setCompanyName(base.name)
          const pe = await penaltyApi.exposure(base.id).catch(() => null)
          setPenalty(pe)
          setStep('result')
          return
        }
        if (authUser?.company_name) setCompanyName(c => c || authUser.company_name || '')
      } catch { /* 복원 실패 → 기존처럼 처음부터 */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const ksicLabel = KSIC_OPTIONS.find(o => o.value === ksic)?.label ?? ''
  const subOptions = ksic ? (KSIC_SUB_OPTIONS[ksic] ?? []) : []
  const workers = Number(workerCount)
  const canDiagnose = !!ksic && workers > 0
  // 사고사례 검색어 — 세부 업종이 있으면 훨씬 정확한 동종 매칭.
  const accQuery = (subIndustry || ksicLabel).trim()
  const industryDisplay = subIndustry || ksicLabel

  const lawChanges = useMemo(() => buildLawChanges(workers, penalty), [workers, penalty])

  // 업종 바꾸면 세부 업종 초기화.
  function pickKsic(v: KsicCategory) {
    setKsic(v)
    setSubIndustry('')
  }

  async function runDiagnosis() {
    if (!canDiagnose || busy) return
    setBusy(true)
    try {
      const base = await sitesApi.ensureDefault().catch(() => null)
      const payload: SiteIn = {
        name: companyName.trim() || base?.name || '내 사업장',
        // business_no 는 SiteOut 에 노출되지 않으므로(프라이버시 — last4 만) 전달하지 않는다.
        // SiteIn 에서 선택 필드라 생략 가능. 진단 폼도 사업자번호를 받지 않는다.
        ksic_code: base?.ksic_code ?? null,
        ksic_category: ksic as KsicCategory,
        ksic_name: subIndustry || ksicLabel,
        address: base?.address ?? null,
        region_office: base?.region_office ?? null,
        open_date: base?.open_date ?? null,
        workers: { ...ZERO_WORKERS, ...(base?.workers ?? {}), regular: workers },
        flags: { ...DEFAULT_FLAGS, ...(base?.flags ?? {}) },
        psm_grade: base?.psm_grade ?? null,
      }
      const site = base?.id ? await sitesApi.update(base.id, payload) : await sitesApi.create(payload)
      setSiteId(site.id)
      // 진단 자체는 과태료 원장(LLM 미사용, 빠름)만 대기 → 결과 화면 즉시.
      const pe = await penaltyApi.exposure(site.id).catch(() => null)
      setPenalty(pe)
      setStep('result')
    } catch {
      toast.error('진단 생성에 실패했어요. 잠시 후 다시 시도해 주세요.')
    } finally { setBusy(false) }
  }

  // 결과 화면 진입 시 동종 사고사례 로드(느릴 수 있어 진단 대기와 분리).
  // 대분류만 고른 경우 검색이 업종을 넘나든다(실측: '제조업' 검색 1순위가 농업 ATV
  // 전복). 뚜렷한 타업종(농림어업·광업) 표지가 있는 사례를 뒤로 미루고, 부족할
  // 때만 채워 넣는다 — 첫 브리핑의 "우리랑 상관없는 사고" 인상 방지.
  useEffect(() => {
    if (step !== 'result' || !accQuery) return
    let cancelled = false
    setAccLoading(true); setAccError(false)
    const FARM_MINE = /농업|원예|경작|축산|어업|어선|양식|임업|벌목|광산|채석|ATV/
    const rank = (cases: AcCase[]): AcCase[] => {
      if (ksic === 'agriculture' || ksic === 'mining') return cases
      const text = (c: AcCase) => `${c.summary} ${c.hazard} ${c.category}`
      const on = cases.filter(c => !FARM_MINE.test(text(c)))
      const off = cases.filter(c => FARM_MINE.test(text(c)))
      return [...on, ...off]
    }
    acSearch(accQuery, 'ko', 1)
      .then(r => { if (!cancelled) setAccCases(rank(r.cases ?? []).slice(0, 6)) })
      .catch(() => { if (!cancelled) setAccError(true) })
      .finally(() => { if (!cancelled) setAccLoading(false) })
    return () => { cancelled = true }
  }, [step, accQuery, ksic])

  // 서류 5종 생성 시작 — 서버 잡(이탈해도 계속). 멱등이라 더블클릭·재시도 안전.
  async function startGenerate() {
    if (!siteId || busy || tasks.length === 0) return
    setBusy(true)
    try {
      const r = await onboardingApi.setupGenerate(siteId, tasks, {}, genTaskName)
      setJobId(r.job_id)
      setJobStatus(null)
      setStep('generating')
    } catch {
      toast.error('생성을 시작하지 못했어요. 잠시 후 다시 시도해 주세요.')
    } finally { setBusy(false) }
  }

  // 생성 잡 폴링 — done/error 에서 완료 화면으로.
  useEffect(() => {
    if (step !== 'generating' || !jobId) return
    let cancelled = false
    const tick = async () => {
      if (pollBusy.current) return
      pollBusy.current = true
      try {
        const st = await onboardingApi.setupStatus(jobId)
        if (cancelled) return
        setJobStatus(st)
        if (st.status !== 'running') {
          setStep('done')
          // 생성 완료 → 위험성평가 항목 확인·선택 팝업(일반 생성기와 동일 UX, 2026-08-09)
          const raDoc = (st.created ?? []).find(c => c.kind === 'risk_assessment' && c.ref_id)
          if (raDoc?.ref_id) { setRaReviewId(raDoc.ref_id); setRaReviewOpen(true) }
        }
      } catch { /* 일시 오류 — 다음 틱에 재시도 */ }
      finally { pollBusy.current = false }
    }
    tick()
    const iv = window.setInterval(tick, 2500)
    return () => { cancelled = true; window.clearInterval(iv) }
  }, [step, jobId])

  /** 온보딩 완료 처리 후 이동. dest: 사업장 홈(기본) 또는 데모 투어. */
  async function completeAnd(dest: 'home' | 'tour', skipped = false) {
    if (busy) return
    setBusy(true)
    try {
      await onboardingApi.complete({ skipped, last_step: STEP_IDX[step], total_steps: STEP_TOTAL })
      qc.invalidateQueries({ queryKey: ['onboarding-status'] })
      if (siteId) {
        qc.invalidateQueries({ queryKey: ['today', siteId] })
        qc.invalidateQueries({ queryKey: ['tasks-overview', siteId] })
        // 진단에서 업종·인원을 업데이트했는데 홈 헤더가 로그인 시점 캐시("업종 미설정 ·
        // 상시 0인")를 보여주는 스테일 방지 — 사이트 상세·목록 재조회.
        qc.invalidateQueries({ queryKey: ['sites'] })
      }
      if (dest === 'home' && siteId) navigate(`/dashboard/site/${siteId}`)
      else onComplete()
    } catch {
      toast.error('완료 처리에 실패했어요.'); setBusy(false)
    }
  }

  const severe = workers >= 5

  return (
    <Modal isOpen={open} onClose={() => { /* 건너뛰기 불가 */ }} hideCloseButton size="xl">
      {step === 'tasks' || step === 'generating' || step === 'done' ? (
        /* ─────────────── STEP 3~5 · 작업 등록+상세(한 화면) → 생성 → 완료 ─────────────── */
        <div className="max-h-[84vh] overflow-y-auto p-6">
          {step === 'tasks' && (
            <TasksStep ksic={ksic || 'other'} value={tasks} onChange={setTasks} workers={workers}
              siteId={siteId} genTask={genTaskName} onGenTaskChange={setGenTaskName}
              onGenerate={startGenerate} busy={busy} />
          )}
          {step === 'generating' && <GeneratingStep status={jobStatus} />}
          {step === 'done' && (
            <DoneStep status={jobStatus} taskCount={tasks.length} busy={busy}
              onGoHome={() => completeAnd('home')} onGoTour={() => completeAnd('tour')} />
          )}
          {step === 'done' && raReviewOpen && raReviewId && siteId && (
            <RaReviewModal siteId={siteId} raId={raReviewId} onClose={() => {
              setRaReviewOpen(false)
              // 검토를 마쳤다는 서버 기록 + **로컬 user 도 즉시 갱신** — 안 하면 같은 SPA
              // 세션의 후속 게이트가 낡은 intro_seen 을 보고 오판한다(실측: 완주 직후
              // 홈에서 첫 이관 팝업이 뜨지 않음).
              onboardingApi.markIntroSeen('ra_review', 0, true)
                .then(r => { const u = useAuthStore.getState().user; if (u) setAuthUser({ ...u, intro_seen: r.intro_seen }) })
                .catch(() => {})
            }} />
          )}
        </div>
      ) : step === 'input' ? (
        /* ─────────────── STEP 1 · 입력 ─────────────── */
        <div className="p-7 space-y-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary/10 px-2.5 py-1 text-[11px] font-semibold text-brand-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-primary" /> 1 / 2 · 30초면 됩니다
            </div>
            <h2 className="text-[22px] font-bold text-ink leading-snug">우리 업종 <span className="text-brand-primary">안전 브리핑</span>,<br/>30초면 받아보실 수 있어요</h2>
            <p className="text-sm text-ink-muted">업종과 인원만 알려주시면 — <b className="text-ink">동종업계 실제 사고사례</b>와 <b className="text-ink">최근 바뀐 법령</b>을 우리 사업장 기준으로 정리해 드립니다.</p>
          </div>

          {/* 업종 */}
          <div>
            <label className="text-sm font-semibold text-ink">우리 업종은? <span className="text-red-500">*</span></label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
              {KSIC_OPTIONS.map(o => {
                const on = ksic === o.value
                return (
                  <button key={o.value} type="button" onClick={() => pickKsic(o.value)}
                    className={`relative text-left rounded-xl border p-3 transition ${
                      on ? 'border-brand-primary bg-brand-primary/[0.08] ring-1 ring-brand-primary/40'
                         : 'border-surface-border hover:border-brand-primary/40 hover:bg-surface-elevated/30'}`}>
                    {on && <FiCheck className="absolute right-2 top-2 text-brand-primary" size={15} />}
                    <div className="text-xl leading-none">{KSIC_ICON[o.value] ?? '📋'}</div>
                    <div className="mt-1.5 text-sm font-semibold text-ink">{o.label}</div>
                    <div className="text-[11px] text-ink-faint truncate">{o.example}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* 세부 업종 — 사고사례 정확도의 핵심 변수(실측: 대분류만 고르면 이업종
              사고가 섞인다). 강제하진 않되 권장을 분명히 보이게 한다. */}
          {subOptions.length > 0 && (
            <div className="rounded-xl border border-brand-primary/25 bg-brand-primary/[0.04] p-3">
              <label className="text-sm font-semibold text-ink">
                우리 일에 가장 가까운 것은? {' '}
                <span className="rounded bg-brand-primary/15 px-1.5 py-0.5 text-[10.5px] font-bold text-brand-primary">권장</span>
                <span className="ml-1 text-ink-faint font-normal text-[12px]">— 골라주시면 우리 업종 사고만 보여드려요</span>
              </label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {subOptions.map(s => {
                  const on = subIndustry === s
                  return (
                    <button key={s} type="button" onClick={() => setSubIndustry(on ? '' : s)}
                      className={`rounded-full px-3 py-1.5 text-[12px] border transition ${
                        on ? 'border-brand-primary text-brand-primary bg-brand-primary/10 font-semibold'
                           : 'border-surface-border text-ink-muted hover:text-ink hover:border-brand-primary/40'}`}>{s}</button>
                  )
                })}
              </div>
            </div>
          )}

          {/* 인원 + 회사명 */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-ink">상시 근로자 수 <span className="text-red-500">*</span></label>
              <div className="mt-2 flex items-center gap-1.5">
                <div className="relative flex-1">
                  <input type="number" min={1} value={workerCount} onChange={e => setWorkerCount(e.target.value)} placeholder="15"
                    className="w-full rounded-xl border border-surface-border bg-surface px-3.5 py-2.5 pr-9 text-sm font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-ink-muted">명</span>
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {[5, 10, 30, 50, 100].map(n => (
                  <button key={n} type="button" onClick={() => setWorkerCount(String(n))}
                    className={`rounded-full px-2.5 py-0.5 text-[11px] border transition ${
                      workerCount === String(n) ? 'border-brand-primary text-brand-primary bg-brand-primary/10' : 'border-surface-border text-ink-muted hover:text-ink'}`}>{n}명</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">회사명 <span className="text-ink-faint font-normal">(선택)</span></label>
              <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="내 사업장"
                className="mt-2 w-full rounded-xl border border-surface-border bg-surface px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
            </div>
          </div>

          <Button variant="primary" className="w-full !py-3 text-[15px]" disabled={!canDiagnose || busy}
            onClick={runDiagnosis} rightIcon={busy ? undefined : <FiArrowRight size={18} />}>
            {busy ? '브리핑 준비 중…' : '무료 안전 브리핑 받기'}
          </Button>
          <p className="text-center text-[11px] text-ink-faint">진단·브리핑은 전액 무료 · 입력 정보로 우리 사업장이 생성됩니다</p>
        </div>
      ) : (
        /* ─────────────── STEP 2 · 안전 브리핑 ─────────────── */
        <div className="max-h-[84vh] overflow-y-auto">
          {/* 헤더 — 차분한 브리핑 톤(겁주기 X) */}
          <div className="relative overflow-hidden bg-gradient-to-br from-brand-primary to-brand-primary/85 px-7 pt-7 pb-6 text-white">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold backdrop-blur">
              <FiShield size={12} /> {industryDisplay} · 상시 {workers}인 맞춤 브리핑
            </div>
            <h2 className="mt-3 text-[22px] font-bold leading-snug">{companyName.trim() || '내 사업장'},<br/>지금 꼭 알아두면 좋은 안전 정보</h2>
            <p className="mt-1.5 text-[13px] text-white/85">동종업계에서 실제로 일어난 사고와 최근 바뀐 법령을 정리했어요. 첫 안전일지는 무료로 만들어 드립니다.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-lg bg-white/12 px-2.5 py-1 text-[12px] font-semibold">
                <FiAlertTriangle size={12} /> 동종 사고사례 {accLoading ? '…' : accCases.length}건
              </span>
              <span className="inline-flex items-center gap-1 rounded-lg bg-white/12 px-2.5 py-1 text-[12px] font-semibold">
                <FiBookOpen size={12} /> 최근 법령 변경 {lawChanges.length}건
              </span>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* ── 동종업계 실제 사고사례 ── */}
            <section>
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-[15px] font-bold text-ink flex items-center gap-1.5">
                  <FiAlertTriangle className="text-red-500" size={16} /> 우리 업종에서 실제로 일어난 사고
                </div>
                <span className="text-[11px] text-ink-faint">회사명은 가려진 공개 사고데이터</span>
              </div>

              {accLoading ? (
                <div className="space-y-2">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="animate-pulse rounded-xl border border-surface-border p-3.5">
                      <div className="h-3 w-20 rounded bg-surface-elevated" />
                      <div className="mt-2 h-3.5 w-full rounded bg-surface-elevated" />
                      <div className="mt-1.5 h-3.5 w-4/5 rounded bg-surface-elevated" />
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 text-[12px] text-ink-faint pt-1"><Spinner size="sm" /> 270만 건 사고데이터에서 우리 업종 사례를 찾는 중…</div>
                </div>
              ) : accCases.length === 0 ? (
                <div className="rounded-xl border border-surface-border bg-surface-elevated/30 p-4 text-center text-[13px] text-ink-muted">
                  {accError ? '사고사례를 불러오지 못했어요. 홈의 사고 레이더에서 다시 확인하실 수 있어요.' : '이 업종에 대한 공개 사고사례가 아직 충분치 않아요.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {accCases.map(c => {
                    const on = !!openCase[c.id]
                    const ra = deriveRA(c)
                    return (
                      <div key={c.id} className={`rounded-xl border transition ${on ? 'border-red-400/50 bg-red-500/[0.03]' : 'border-surface-border hover:border-red-400/40'}`}>
                        <button type="button" onClick={() => setOpenCase(s => ({ ...s, [c.id]: !on }))}
                          className="w-full text-left px-3.5 py-3 flex items-start gap-2.5">
                          <span className="mt-0.5 shrink-0 rounded-md bg-red-500/12 px-2 py-0.5 text-[10.5px] font-bold text-red-500">{c.category || '사고'}</span>
                          <span className={`min-w-0 flex-1 text-[13.5px] text-ink leading-relaxed ${on ? '' : 'line-clamp-2'}`}>{c.summary}</span>
                          {/* 각 사고마다 위험성평가 등급 배지(한눈에) */}
                          <span className={`mt-0.5 shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${LEVEL_STYLE[ra.level]}`}>위험성 {ra.level}</span>
                          <FiChevronDown className={`mt-0.5 shrink-0 text-ink-faint transition-transform ${on ? 'rotate-180' : ''}`} size={16} />
                        </button>
                        {on && (
                          <div className="px-3.5 pb-3.5 pt-0.5 space-y-2.5">
                            {c.hazard && (
                              <div className="rounded-lg bg-red-500/[0.06] border border-red-400/20 p-3">
                                <div className="text-[11.5px] font-bold text-red-500 mb-1 flex items-center gap-1"><FiAlertTriangle size={12} /> 사고 원인</div>
                                <div className="text-[13px] text-ink leading-relaxed">{c.hazard}</div>
                              </div>
                            )}

                            {/* 사고사례 → 우리 위험성평가 반영 (예시) */}
                            <div className="rounded-lg bg-brand-primary/[0.05] border border-brand-primary/25 p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-[11.5px] font-bold text-brand-primary flex items-center gap-1"><FiClipboard size={12} /> 우리 사업장 위험성평가 반영 (예시)</div>
                                <span className="rounded bg-brand-primary/12 px-1.5 py-0.5 text-[10px] font-semibold text-brand-primary">자동 반영</span>
                              </div>
                              <div className="text-[12.5px] text-ink leading-relaxed">
                                <b className="text-ink-muted">위험요인</b> · {c.hazard || c.summary}
                              </div>
                              {/* 매트릭스: 가능성 × 중대성 = 위험성 → 개선 후 */}
                              <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[11.5px]">
                                <span className="text-ink-muted">가능성 <b className="text-ink">{ra.freq}</b> × 중대성 <b className="text-ink">{ra.sev}</b> =</span>
                                <span className={`rounded-md border px-2 py-0.5 font-bold ${LEVEL_STYLE[ra.level]}`}>{ra.score}점 · {ra.level}</span>
                                <FiArrowRight size={11} className="text-ink-faint" />
                                <span className="text-ink-muted">개선 후</span>
                                <span className={`rounded-md border px-2 py-0.5 font-bold ${LEVEL_STYLE[ra.rLevel]}`}>{ra.rScore}점 · {ra.rLevel}</span>
                              </div>
                              {/* 감소대책(사고의 재발방지 대책을 대책 tier 로 반영) */}
                              {c.prevention && (
                                <div className="mt-2 flex items-start gap-1.5 text-[12px] text-ink">
                                  <span className="mt-0.5 shrink-0 rounded bg-emerald-500/12 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600">{TIER_LABEL[ra.tier]} 대책</span>
                                  <span className="leading-relaxed">{c.prevention}</span>
                                </div>
                              )}
                              <div className="mt-2 text-[10.5px] text-ink-faint leading-relaxed">이 사고가 우리 위험성평가 항목으로 반영된 예시입니다. 가입 후 실제 평가에 자동으로 추가·관리됩니다.</div>
                            </div>

                            {c.source_label && <div className="text-[11px] text-ink-faint">출처 · {c.source_label}</div>}
                          </div>
                        )}
                      </div>
                    )
                  })}
                  <a href="/ac-datacenter" target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-primary hover:underline pt-0.5">
                    <FiSearch size={13} /> 사고사례 더 찾아보기
                  </a>
                </div>
              )}
            </section>

            {/* ── 최근 법령 변경 ── */}
            <section>
              <div className="text-[15px] font-bold text-ink flex items-center gap-1.5 mb-2.5">
                <FiBookOpen className="text-brand-primary" size={16} /> 최근 바뀐 법령 — 우리 사업장은?
              </div>
              <div className="space-y-2">
                {lawChanges.map(l => {
                  const on = !!openLaw[l.id]
                  const tone = l.badgeTone === 'red'
                    ? 'bg-red-500/12 text-red-500'
                    : 'bg-amber-500/15 text-amber-600'
                  return (
                    <div key={l.id} className="rounded-xl border border-surface-border">
                      <div className="px-3.5 pt-3 pb-2.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`rounded-md px-2 py-0.5 text-[10.5px] font-bold ${tone}`}>{l.badge}</span>
                          {l.applicableNow && <span className="rounded-md bg-red-500/12 px-2 py-0.5 text-[10.5px] font-bold text-red-500">이미 적용 중</span>}
                          {l.liveEffective && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[10.5px] font-bold text-amber-600">
                              <FiClock size={11} /> {l.liveEffective}
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 text-[14px] font-bold text-ink">{l.title}</div>
                        <div className="text-[11px] text-ink-faint mt-0.5">{l.article} · {l.effective}</div>
                        {/* 우리 사업장 영향 — 항상 노출(개인화) */}
                        <div className="mt-2 rounded-lg bg-brand-primary/[0.06] border border-brand-primary/20 px-3 py-2 text-[12.5px] text-ink leading-relaxed">
                          <b className="text-brand-primary">우리 사업장</b> · {l.impact}
                        </div>
                      </div>
                      <button type="button" onClick={() => setOpenLaw(s => ({ ...s, [l.id]: !on }))}
                        className="w-full flex items-center justify-center gap-1 border-t border-surface-border py-2 text-[12px] font-medium text-ink-muted hover:text-ink">
                        {on ? '접기' : '무엇이 바뀌나 · 어떻게 대응하나'}
                        <FiChevronDown className={`transition-transform ${on ? 'rotate-180' : ''}`} size={14} />
                      </button>
                      {on && (
                        <div className="px-3.5 pb-3.5 space-y-2.5">
                          <div className="rounded-lg bg-surface-elevated/40 p-3">
                            <div className="text-[11.5px] font-bold text-ink-muted mb-1">무엇이 바뀌나</div>
                            <div className="text-[13px] text-ink leading-relaxed">{l.what}</div>
                          </div>
                          <div className="rounded-lg bg-emerald-500/[0.06] border border-emerald-400/20 p-3">
                            <div className="text-[11.5px] font-bold text-emerald-600 mb-1 flex items-center gap-1"><FiCheck size={12} /> 어떻게 대응하나</div>
                            <div className="text-[13px] text-ink leading-relaxed">{l.action}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {severe && (
                <p className="mt-2 text-[11px] text-ink-faint leading-relaxed">
                  * 위 내용은 일반적인 법령 안내입니다. 우리 사업장에 정확히 어떤 의무가 적용되는지는 가입 후 사업장 관리에서 자동으로 진단됩니다.
                </p>
              )}
            </section>

            {/* ── 전환 — 다음 단계: 작업 등록 + 기준 서류 4종 무료 생성(작업 1개) ── */}
            <section className="rounded-2xl border border-brand-primary/30 bg-gradient-to-br from-brand-primary/[0.08] to-transparent p-4 space-y-1.5">
              <div className="text-sm font-bold text-ink flex items-center gap-1.5"><FiFileText className="text-brand-primary" /> 이제 우리 사업장 서류를 만들 차례예요</div>
              <p className="text-[12px] text-ink-muted">
                다음 단계에서 우리 작업을 체크하면 — 위 법령 대응의 출발점인 <b className="text-ink">위험성평가 · JSA · 작업허가서 · 법규 준수 점검</b>
                기준 서류 4종을 골라주신 작업 1개에 대해 <b className="text-ink">무료(사용량 미차감)</b>로 한 번에 만들어 드립니다.
              </p>
            </section>

            <div className="flex items-center gap-2 pt-1">
              <button onClick={() => setStep('input')} className="rounded-xl border border-surface-border px-3 py-3 text-ink-muted hover:text-ink" title="다시 입력">
                <FiChevronLeft size={18} />
              </button>
              <Button variant="primary" className="flex-1 !py-3 text-[15px]" disabled={busy} onClick={() => setStep('tasks')}
                rightIcon={<FiArrowRight size={18} />}>
                다음 · 우리 작업 등록하고 서류 만들기
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
